import React, { useRef, useState, useLayoutEffect, useImperativeHandle, forwardRef } from 'react';
import ReactDOM from 'react-dom';
import { PortalContainerProvider } from 'App/shadcn/lib/portal-context';
// @ts-expect-error — ?shadow query returns a string via css-loader exportType:'string'
import tailwindCSS from 'App/styles/tailwind.css?shadow';

type Props = React.HTMLAttributes<HTMLDivElement> & {
	children: React.ReactNode;
};

/**
 * Renders children inside a Shadow DOM boundary.
 * All legacy CSS is blocked; only Tailwind/shadcn styles apply inside.
 */
const ShadcnScope = forwardRef<HTMLDivElement, Props>(({ children, ...divProps }, ref) => {
	const hostRef = useRef<HTMLDivElement>(null);
	const [container, setContainer] = useState<HTMLDivElement | null>(null);

	useImperativeHandle(ref, () => hostRef.current as HTMLDivElement);

	useLayoutEffect(() => {
		const host = hostRef.current;
		if (!host) return;

		// Reuse an existing shadow root rather than bailing. After an HMR
		// remount React may reattach to a host node that already owns a shadow
		// root while this component's `container` state is reset to null —
		// bailing here would leave the portal unmounted and render nothing.
		let shadow = host.shadowRoot;
		if (!shadow) {
			shadow = host.attachShadow({ mode: 'open' });

			const style = document.createElement('style');
			style.textContent = tailwindCSS;
			shadow.appendChild(style);

			const root = document.createElement('div');
			root.style.display = 'contents';
			shadow.appendChild(root);
		}

		const portalRoot = shadow.querySelector('div');
		// Radix portals (sheets, dropdowns, tooltips) mount on this container,
		// OUTSIDE any .ai-ws/.praxis scope in the app tree below it. The AI
		// token sheets also define their vars on [data-ai-tokens] (WEB-8001),
		// so portaled content inherits them instead of collapsing. Inert in
		// scopes that don't inject those sheets. Set here (not at creation) so
		// HMR-reused shadow roots pick it up too.
		portalRoot?.setAttribute('data-ai-tokens', '');
		setContainer(portalRoot);
	}, []);

	return (
		<div ref={hostRef} {...divProps}>
			{container && (
				<PortalContainerProvider value={container}>
					{ReactDOM.createPortal(children, container)}
				</PortalContainerProvider>
			)}
		</div>
	);
});

ShadcnScope.displayName = 'ShadcnScope';

export default ShadcnScope;
